'use client'

import { useCallback, useMemo, useState } from 'react'
import type { Locale } from '@/root/shared-types'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { modify } from '@/app/actions/reservation-modification'
import { MODIFY_ERROR_CODES } from '@/root/shared-types/api/reservation'
import { ReservationWithModification, SailPackageRef } from '../types'
import { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import { handleStaleServerAction } from '../utils'

const findGuestSeqNForPassenger = (params: {
  reservation: ReservationWithModification
  sailPackageSeqN: number
  priceCode: string
  slotIndex: number
}): number | null => {
  const owners = (params.reservation.items ?? [])
    .filter(
      (item) =>
        item.sailPackageSeqN === params.sailPackageSeqN &&
        item.priceCategory === params.priceCode &&
        item.type === 'PASSENGER'
    )
    .flatMap((item) => item.ownerSeqNs ?? [])

  return owners[params.slotIndex] ?? null
}

interface UseTicketEditStateParams {
  reservationToken: string | undefined
  currentReservation: ReservationWithModification
  ticketSailPackageSeqN: number
  additionalTicketSailPackages: SailPackageRef[]
  locale: Locale
  getTranslation: (key: Key) => string
  onReservationUpdated: (updated: ReservationWithModification) => void
  onError: (message: string | null) => void
}

export interface UseTicketEditStateReturn {
  isSaving: boolean
  isOperationPending: boolean
  hasUnsavedOutboundForms: boolean
  hasUnsavedReturnForms: boolean
  hasUnsavedPassengerForms: boolean
  setIsOperationPending: (pending: boolean) => void
  setHasUnsavedOutboundForms: (hasUnsaved: boolean) => void
  setHasUnsavedReturnForms: (hasUnsaved: boolean) => void
  handleTicketSaved: (updated: ReservationWithModification) => void
  handleTicketError: (message: string, code?: string, status?: number) => void
  clearError: () => void
  handleSavePassengerInfoOutbound: (params: {
    priceCode: string
    slotIndex: number
    data: PassengerFormData
  }) => Promise<void>
  handleSavePassengerInfoReturn:
    | ((params: { priceCode: string; slotIndex: number; data: PassengerFormData }) => Promise<void>)
    | null
  isRoundTrip: boolean
  returnSailPackage: SailPackageRef | null
}

export function useTicketEditState({
  reservationToken,
  currentReservation,
  ticketSailPackageSeqN,
  additionalTicketSailPackages,
  locale,
  getTranslation,
  onReservationUpdated,
  onError,
}: UseTicketEditStateParams): UseTicketEditStateReturn {
  const buildErrorMessage = useCallback(
    (message: string, code?: string) => {
      const errorCodeLabel = getTranslation('errorRedCodePrefix')
      return code ? `${message} ${errorCodeLabel} ${code}` : message
    },
    [getTranslation]
  )

  const [isSaving, setIsSaving] = useState(false)
  const [isOperationPending, setIsOperationPending] = useState(false)
  const [hasUnsavedOutboundForms, setHasUnsavedOutboundForms] = useState(false)
  const [hasUnsavedReturnForms, setHasUnsavedReturnForms] = useState(false)
  const hasUnsavedPassengerForms = hasUnsavedOutboundForms || hasUnsavedReturnForms

  const makeHandleSavePassengerInfo = useCallback(
    (directionSeqN: number) => async (params: { priceCode: string; slotIndex: number; data: PassengerFormData }) => {
      if (!reservationToken) return

      const guestSeqN =
        params.data.seqN ??
        findGuestSeqNForPassenger({
          reservation: currentReservation,
          sailPackageSeqN: directionSeqN,
          priceCode: params.priceCode,
          slotIndex: params.slotIndex,
        })

      if (guestSeqN === null) {
        onError(getTranslation('editErrorPassengerResolve'))
        return
      }

      setIsSaving(true)
      onError(null)

      let setGuestInfoResult: Awaited<ReturnType<typeof modify>>
      try {
        setGuestInfoResult = await modify(
          reservationToken,
          [
            {
              __type: 'setGuestInfo',
              guestSeqN,
              firstName: params.data.firstName,
              lastName: params.data.lastName,
              citizenship: params.data.citizenship,
              birthday: params.data.birthdate,
              gender: params.data.gender,
              comments: params.data.additionalInfo,
              ...(params.data.personalId && { personalIdentificationNumber: params.data.personalId }),
            },
          ],
          locale
        )
      } catch (err) {
        if (handleStaleServerAction({ error: err, getTranslation, onError: (message) => onError(message) })) {
          setIsSaving(false)
          return
        }

        throw err
      }

      if (!setGuestInfoResult.success) {
        onError(
          setGuestInfoResult.error.code === MODIFY_ERROR_CODES.INVALID_PERSONAL_IDENTIFICATION_NUMBER
            ? getTranslation('INVALID_PERSONAL_IDENTIFICATION_NUMBER')
            : (setGuestInfoResult.error.message ?? getTranslation('editErrorSaveCustomerFailed'))
        )
        setIsSaving(false)
        return
      }

      onReservationUpdated(setGuestInfoResult.data)
      setIsSaving(false)
      onError(null)
    },
    [reservationToken, currentReservation, locale, getTranslation, onReservationUpdated, onError]
  )

  const isRoundTrip = additionalTicketSailPackages.length > 0
  const returnSailPackage = additionalTicketSailPackages[0] ?? null

  const handleSavePassengerInfoOutbound = useMemo(
    () => makeHandleSavePassengerInfo(ticketSailPackageSeqN),
    [makeHandleSavePassengerInfo, ticketSailPackageSeqN]
  )

  const handleSavePassengerInfoReturn = useMemo(
    () => (returnSailPackage ? makeHandleSavePassengerInfo(returnSailPackage.seqN) : null),
    [makeHandleSavePassengerInfo, returnSailPackage]
  )

  const handleTicketSaved = useCallback(
    (updated: ReservationWithModification) => {
      onReservationUpdated(updated)
    },
    [onReservationUpdated]
  )

  const handleTicketError = useCallback(
    (message: string, code?: string) => {
      onError(buildErrorMessage(message, code))
    },
    [onError, buildErrorMessage]
  )

  const clearError = useCallback(() => onError(null), [onError])

  return {
    isSaving,
    isOperationPending,
    hasUnsavedOutboundForms,
    hasUnsavedReturnForms,
    hasUnsavedPassengerForms,
    setIsOperationPending,
    setHasUnsavedOutboundForms,
    setHasUnsavedReturnForms,
    handleTicketSaved,
    handleTicketError,
    clearError,
    handleSavePassengerInfoOutbound,
    handleSavePassengerInfoReturn,
    isRoundTrip,
    returnSailPackage,
  }
}
